// Cheap bloom post-process (WebGL2).
//
// The scene is rendered into an offscreen colour+depth FBO, then a bright-pass
// extracts the glowing pixels into a half-res buffer which is blurred (separable
// Gaussian, two ping-pong iterations) and added back on top. This is what turns
// additive "bright blobs" into the soft modern glow the art references show.
//
// Presentation-only; it never touches the deterministic sim.

const QUAD_VS = `#version 300 es
  layout(location=0) in vec2 a_pos;
  out vec2 v_uv;
  void main() {
    v_uv = a_pos * 0.5 + 0.5;
    gl_Position = vec4(a_pos, 0.0, 1.0);
  }`;

const BRIGHT_FS = `#version 300 es
  precision mediump float;
  in vec2 v_uv;
  uniform sampler2D u_tex;
  uniform float u_threshold;
  uniform float u_knee;
  out vec4 outColor;
  void main() {
    vec3 c = texture(u_tex, v_uv).rgb;
    float l = max(max(c.r, c.g), c.b);
    float k = clamp((l - u_threshold) / max(u_knee, 1e-4), 0.0, 1.0);
    outColor = vec4(c * k, 1.0);
  }`;

const BLUR_FS = `#version 300 es
  precision mediump float;
  in vec2 v_uv;
  uniform sampler2D u_tex;
  uniform vec2 u_dir; // texel-scaled direction
  out vec4 outColor;
  void main() {
    float w0 = 0.227027, w1 = 0.194595, w2 = 0.121622, w3 = 0.054054, w4 = 0.016216;
    vec3 s = texture(u_tex, v_uv).rgb * w0;
    s += texture(u_tex, v_uv + u_dir * 1.0).rgb * w1;
    s += texture(u_tex, v_uv - u_dir * 1.0).rgb * w1;
    s += texture(u_tex, v_uv + u_dir * 2.0).rgb * w2;
    s += texture(u_tex, v_uv - u_dir * 2.0).rgb * w2;
    s += texture(u_tex, v_uv + u_dir * 3.0).rgb * w3;
    s += texture(u_tex, v_uv - u_dir * 3.0).rgb * w3;
    s += texture(u_tex, v_uv + u_dir * 4.0).rgb * w4;
    s += texture(u_tex, v_uv - u_dir * 4.0).rgb * w4;
    outColor = vec4(s, 1.0);
  }`;

const COMPOSITE_FS = `#version 300 es
  precision highp float;
  in vec2 v_uv;
  uniform sampler2D u_scene;
  uniform sampler2D u_bloom;
  uniform float u_intensity;
  uniform float u_warp;   // 0 = rectilinear, 1 = panini
  uniform float u_d;      // Panini strength: 0 = rectilinear, 1 = classic
  uniform float u_hfov;   // horizontal FOV (radians)
  uniform float u_vfov;   // vertical FOV (radians, derived from aspect)
  out vec4 outColor;

  // Invert the generalized Pannini projection to get the rectilinear source UV
  // for an output pixel. d = 0 is exactly rectilinear (identity), d = 1 is the
  // classic Panini; fractional values blend the two. Verticals stay straight,
  // radial lines through the centre stay straight, the centre is magnified and
  // the periphery compressed (removing rectilinear edge stretch); higher d
  // magnifies the centre more. The vertical scale is chosen so the mapping
  // stays isotropic at the centre and the four output corners land on the four
  // source corners, so the whole frame is covered with no cropping.
  vec2 paniniUv(vec2 ndc) {
    float d = max(u_d, 0.0);
    float hh = max(u_hfov, 0.01) * 0.5;
    float hv = max(u_vfov, 0.01) * 0.5;
    float tanH = max(tan(hh), 1e-4);
    float tanV = max(tan(hv), 1e-4);
    float hmax = (d + 1.0) * sin(hh) / (d + cos(hh));
    float vmax = tanV * hmax / tanH;
    float h = ndc.x * hmax;
    float v = ndc.y * vmax;
    float K = (h / (d + 1.0)) * (h / (d + 1.0));
    float cosPhi = (-K * d + sqrt(max(0.0, 1.0 + K * (1.0 - d * d)))) / (K + 1.0);
    cosPhi = clamp(cosPhi, -1.0, 1.0);
    float phi = sign(h) * acos(cosPhi);
    float theta = atan(v * (d + cosPhi) / (d + 1.0));
    float u = tan(phi) / tanH;
    float w = tan(theta) / (cosPhi * tanV);
    return vec2(u * 0.5 + 0.5, w * 0.5 + 0.5);
  }

  void main() {
    vec2 uv = v_uv;
    if (u_warp > 0.5) uv = paniniUv(v_uv * 2.0 - 1.0);
    vec3 s = texture(u_scene, uv).rgb;
    vec3 b = texture(u_bloom, uv).rgb;
    outColor = vec4(s + b * u_intensity, 1.0);
  }`;

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    throw new Error("bloom shader: " + gl.getShaderInfoLog(s));
  }
  return s;
}

export class Bloom {
  private gl: WebGL2RenderingContext;
  enabled = true;
  /** Panini warp in the final composite (works with bloom off). */
  panini = false;
  /** Panini strength: 0 = rectilinear, 1 = classic (see `setPanini`). */
  paniniD = 1;
  /** Horizontal FOV for the warp, radians. */
  hfovRad = (130 * Math.PI) / 180;
  /** Vertical FOV for the warp, radians (must match the camera projection). */
  vfovRad = (100 * Math.PI) / 180;
  /** Targets failed a completeness check (e.g. a 0-sized canvas): the renderer
   *  falls back to drawing straight to the canvas. */
  private broken = false;
  intensity = 0.9;
  threshold = 0.7;
  knee = 0.6;

  private w = 1;
  private h = 1;
  private bw = 1;
  private bh = 1;

  private sceneFbo: WebGLFramebuffer | null = null;
  private sceneTex: WebGLTexture | null = null;
  private sceneDepth: WebGLRenderbuffer | null = null;
  private aFbo: WebGLFramebuffer | null = null;
  private aTex: WebGLTexture | null = null;
  private bFbo: WebGLFramebuffer | null = null;
  private bTex: WebGLTexture | null = null;

  private brightProgram: WebGLProgram;
  private blurProgram: WebGLProgram;
  private compositeProgram: WebGLProgram;
  private quadVao: WebGLVertexArrayObject;

  // Uniform locations.
  private brightTex: WebGLUniformLocation | null;
  private brightThreshold: WebGLUniformLocation | null;
  private brightKnee: WebGLUniformLocation | null;
  private blurTex: WebGLUniformLocation | null;
  private blurDir: WebGLUniformLocation | null;
  private compScene: WebGLUniformLocation | null;
  private compBloom: WebGLUniformLocation | null;
  private compIntensity: WebGLUniformLocation | null;
  private compWarp: WebGLUniformLocation | null;
  private compPaniniD: WebGLUniformLocation | null;
  private compHfov: WebGLUniformLocation | null;
  private compVfov: WebGLUniformLocation | null;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    this.brightProgram = this.link(QUAD_VS, BRIGHT_FS);
    this.blurProgram = this.link(QUAD_VS, BLUR_FS);
    this.compositeProgram = this.link(QUAD_VS, COMPOSITE_FS);
    this.brightTex = gl.getUniformLocation(this.brightProgram, "u_tex");
    this.brightThreshold = gl.getUniformLocation(this.brightProgram, "u_threshold");
    this.brightKnee = gl.getUniformLocation(this.brightProgram, "u_knee");
    this.blurTex = gl.getUniformLocation(this.blurProgram, "u_tex");
    this.blurDir = gl.getUniformLocation(this.blurProgram, "u_dir");
    this.compScene = gl.getUniformLocation(this.compositeProgram, "u_scene");
    this.compBloom = gl.getUniformLocation(this.compositeProgram, "u_bloom");
    this.compIntensity = gl.getUniformLocation(this.compositeProgram, "u_intensity");
    this.compWarp = gl.getUniformLocation(this.compositeProgram, "u_warp");
    this.compPaniniD = gl.getUniformLocation(this.compositeProgram, "u_d");
    this.compHfov = gl.getUniformLocation(this.compositeProgram, "u_hfov");
    this.compVfov = gl.getUniformLocation(this.compositeProgram, "u_vfov");

    // Fullscreen triangle.
    this.quadVao = gl.createVertexArray()!;
    gl.bindVertexArray(this.quadVao);
    const vbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
  }

  private link(vs: string, fs: string): WebGLProgram {
    const gl = this.gl;
    const p = gl.createProgram()!;
    gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, vs));
    gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      throw new Error("bloom link: " + gl.getProgramInfoLog(p));
    }
    return p;
  }

  private makeTarget(w: number, h: number, withDepth: boolean) {
    const gl = this.gl;
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const fb = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    let depth: WebGLRenderbuffer | null = null;
    if (withDepth) {
      depth = gl.createRenderbuffer()!;
      gl.bindRenderbuffer(gl.RENDERBUFFER, depth);
      gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, w, h);
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, depth);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return { fb, tex, depth };
  }

  /** Can the post-process run this frame? (enabled or panini, targets complete) */
  usable(): boolean {
    return (this.enabled || this.panini) && !this.broken && this.sceneFbo !== null;
  }

  /** Enable the panini composite warp (`d` = 0 rectilinear .. 1 classic). */
  setPanini(on: boolean, hfovRad: number, vfovRad: number, d = 1) {
    this.panini = on;
    if (Number.isFinite(hfovRad) && hfovRad > 0) this.hfovRad = hfovRad;
    if (Number.isFinite(vfovRad) && vfovRad > 0) this.vfovRad = vfovRad;
    if (Number.isFinite(d)) this.paniniD = Math.max(0, d);
  }

  private targetsComplete(): boolean {
    const gl = this.gl;
    for (const fb of [this.sceneFbo, this.aFbo, this.bFbo]) {
      if (!fb) return false;
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        return false;
      }
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return true;
  }

  resize(w: number, h: number) {
    const gl = this.gl;
    // Never build 0-sized targets (a hidden/unsized canvas): incomplete
    // framebuffers make every draw fail -> a black screen.
    if (!(w > 0) || !(h > 0)) return;
    if (w === this.w && h === this.h && this.sceneFbo) return;
    this.w = w;
    this.h = h;
    this.bw = Math.max(1, w >> 1);
    this.bh = Math.max(1, h >> 1);
    const del = (fb: WebGLFramebuffer | null, tex: WebGLTexture | null, depth: WebGLRenderbuffer | null) => {
      if (fb) gl.deleteFramebuffer(fb);
      if (tex) gl.deleteTexture(tex);
      if (depth) gl.deleteRenderbuffer(depth);
    };
    del(this.sceneFbo, this.sceneTex, this.sceneDepth);
    del(this.aFbo, this.aTex, null);
    del(this.bFbo, this.bTex, null);
    const scene = this.makeTarget(w, h, true);
    const a = this.makeTarget(this.bw, this.bh, false);
    const b = this.makeTarget(this.bw, this.bh, false);
    this.sceneFbo = scene.fb; this.sceneTex = scene.tex; this.sceneDepth = scene.depth;
    this.aFbo = a.fb; this.aTex = a.tex;
    this.bFbo = b.fb; this.bTex = b.tex;
    this.broken = !this.targetsComplete();
    if (this.broken) {
      console.warn("[bloom] framebuffer incomplete — rendering without bloom");
    }
  }

  /** Keep the FBOs matched to the live GL drawing buffer (handles late resizes). */
  private ensureSize() {
    const gl = this.gl;
    const w = gl.drawingBufferWidth || this.w;
    const h = gl.drawingBufferHeight || this.h;
    if (w !== this.w || h !== this.h || !this.sceneFbo) this.resize(w, h);
  }

  /** Bind the scene FBO + full-res viewport (call before the scene passes). */
  beginScene() {
    const gl = this.gl;
    this.ensureSize();
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.sceneFbo);
    gl.viewport(0, 0, this.w, this.h);
  }

  private bindTex(unit: number, tex: WebGLTexture | null, loc: WebGLUniformLocation | null) {
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    if (loc) gl.uniform1i(loc, unit);
  }

  /** Bright-pass + blur + composite to the default framebuffer. */
  apply() {
    const gl = this.gl;
    this.ensureSize();
    if (!this.sceneFbo) return;
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(this.quadVao);

    // Bloom uses dedicated texture units (4/5) so it never disturbs the units the
    // world/models/sky/particles sample (0-3): touching those would both clobber
    // bindings and create a feedback loop (sampling the FBO's own attachment).
    const SCENE_UNIT = 4;
    const BLOOM_UNIT = 5;

    // Bright-pass: scene -> A (half res). Skip the whole bloom chain when bloom
    // is off but panini is on (we still need the scene FBO + composite warp).
    if (this.enabled) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, this.aFbo);
      gl.viewport(0, 0, this.bw, this.bh);
      gl.useProgram(this.brightProgram);
      this.bindTex(SCENE_UNIT, this.sceneTex, this.brightTex);
      if (this.brightThreshold) gl.uniform1f(this.brightThreshold, this.threshold);
      if (this.brightKnee) gl.uniform1f(this.brightKnee, this.knee);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      // Separable Gaussian, two ping-pong iterations (A -> B -> A). Each pass
      // samples the *other* target, so there's no feedback loop.
      gl.useProgram(this.blurProgram);
      for (let i = 0; i < 2; i++) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.bFbo);
        gl.viewport(0, 0, this.bw, this.bh);
        this.bindTex(SCENE_UNIT, this.aTex, this.blurTex);
        if (this.blurDir) gl.uniform2f(this.blurDir, 1 / this.bw, 0);
        gl.drawArrays(gl.TRIANGLES, 0, 3);

        gl.bindFramebuffer(gl.FRAMEBUFFER, this.aFbo);
        this.bindTex(SCENE_UNIT, this.bTex, this.blurTex);
        if (this.blurDir) gl.uniform2f(this.blurDir, 0, 1 / this.bh);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      }
    }

    // Composite: scene + glow -> default framebuffer.
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.w, this.h);
    gl.useProgram(this.compositeProgram);
    this.bindTex(SCENE_UNIT, this.sceneTex, this.compScene);
    this.bindTex(BLOOM_UNIT, this.enabled ? this.aTex : this.sceneTex, this.compBloom);
    if (this.compIntensity) gl.uniform1f(this.compIntensity, this.enabled ? this.intensity : 0);
    if (this.compWarp) gl.uniform1f(this.compWarp, this.panini ? 1 : 0);
    if (this.compPaniniD) gl.uniform1f(this.compPaniniD, this.paniniD);
    if (this.compHfov) gl.uniform1f(this.compHfov, this.hfovRad);
    if (this.compVfov) gl.uniform1f(this.compVfov, this.vfovRad);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    gl.bindVertexArray(null);
    gl.enable(gl.DEPTH_TEST);
  }
}
